import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Form from './Form';
import useRegisterValidationFields from '../../hooks/useRegisterValidationFields';
import { bindTransportUsageTypesSelectOption } from '../../helpers/selectHelpers';
import ContractRiskAmount from '../ContractRiskAmount';
import ContractDefaultCoveragesDescription from '../ContractDefaultCoveragesDescription';
import { bindRoadCarePlanDefaultCoveragesDescription } from '../../helpers/roadCare/roadCareCalcHelper';
import useDeviceResize from '../../hooks/useDeviceResize';
import { EFES_CONTENT_WIDTH, FULL_WIDTH } from '../../constants/uiConstants';
import StepNavigationButtons from '../MUI/StepNavigationButtons';

import RoadCareCalculatorWrapperImage from '../../img/roadCareCalculatorWrapperImage.svg';
import useStyles from './makeStyles';
import useStepNavigationAmountStyles from '../MUI/StepNavigationAmount/makeStyles';

const RoadCareCalculator = ({
  formProps,
  dispatchReleaseYear,
  dispatchTransportUsageType,
  buyButtonHandler,
  totalPriceAfterDiscount,
  handleOpenMobileSummary,
  isFetchingPrice,
  showNavigationButtons,
  showForm,
}) => {
  const { t, i18n } = useTranslation();
  const isDeviceMdSize = useDeviceResize('md');
  const isDeviceLgSize = useDeviceResize('extraLgSize');
  const classes = useStyles({ isDeviceMdSize });
  const stepNavigationAmountClasses = useStepNavigationAmountStyles();

  const { transportUsageTypes } = useSelector((state) => state.vehiclesReducer);
  const {
    defaultCoverages,
    riskAmount,
    isFetchingRiskAmounts,
  } = useSelector((state) => state.roadCareCalculation);
  const { register, watch, setValue, getValues } = formProps;
  watch('releaseYear');

  useRegisterValidationFields(['releaseYear', 'transportUseType'], register);

  const currentPlanDescriptions = useMemo(
    () => bindRoadCarePlanDefaultCoveragesDescription(defaultCoverages, i18n.language),
    [defaultCoverages, i18n.language],
  );

  useEffect(() => {
    if (transportUsageTypes.length) {
      const defaultTransportUsageType = bindTransportUsageTypesSelectOption(transportUsageTypes)[0];
      setValue('transportUseType', defaultTransportUsageType, true);
      dispatchTransportUsageType(defaultTransportUsageType);
    }
  }, [dispatchTransportUsageType, setValue, transportUsageTypes]);

  const handleChangeReleaseYear = (data) => {
    setValue('releaseYear', data, true);
    dispatchReleaseYear(data);
  };

  return (
    <>
      <div className={classes.iconWrapper}><img src={RoadCareCalculatorWrapperImage} alt="" className={classes.icon} /></div>
      {
        showForm && (
          <div className={classes.formWrapper}>
            <Form
              formProps={formProps}
              releaseYear={getValues().releaseYear}
              handleChangeReleaseYear={handleChangeReleaseYear}
              transportUseType={getValues().transportUseType}
              disabledUseTypeInput
            />
          </div>
        )
      }
      <Grid container item xs={12} xl={12} className={classes.programDetails}>
        <Grid item xs={12} md={5} className={classes.contractRiskAmountWrapper}>
          {
            !!riskAmount && (
              <ContractRiskAmount
                amount={riskAmount}
                isFetchingPrice={isFetchingRiskAmounts}
                classes={classes}
                infoTitle={t('insurance.riskAmount')}
              />
            )
          }
        </Grid>
        <Grid item xs={12} md={7}>
          <ContractDefaultCoveragesDescription descriptions={currentPlanDescriptions} />
        </Grid>
      </Grid>
      {showNavigationButtons && isDeviceLgSize && (
        <Grid
          container
          className={classNames({
            [stepNavigationAmountClasses.mobileNavigation]: isDeviceLgSize,
          })}
          spacing={isDeviceLgSize ? 4 : 0}
        >
          <Grid item style={{ width: isDeviceLgSize ? FULL_WIDTH : EFES_CONTENT_WIDTH }}>
            <Box>
              <StepNavigationButtons
                btnText={t('buyOnline')}
                amount={totalPriceAfterDiscount}
                handleOpenMobileSummary={handleOpenMobileSummary}
                buttonHandler={buyButtonHandler}
                isFetchingPrice={isFetchingPrice}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

RoadCareCalculator.propTypes = {
  formProps: PropTypes.shape({
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    errors: PropTypes.shape({}),
    clearError: PropTypes.func,
    watch: PropTypes.func,
  }),
  totalPriceAfterDiscount: PropTypes.number,
  isFetchingPrice: PropTypes.bool,
  dispatchTransportUsageType: PropTypes.func,
  dispatchReleaseYear: PropTypes.func,
  buyButtonHandler: PropTypes.func,
  handleOpenMobileSummary: PropTypes.func,
  showNavigationButtons: PropTypes.bool,
  showForm: PropTypes.bool,
};

RoadCareCalculator.defaultProps = {
  showNavigationButtons: true,
  handleOpenMobileSummary: () => { },
  dispatchReleaseYear: () => { },
  dispatchTransportUsageType: () => { },
  buyButtonHandler: () => { },
  totalPriceAfterDiscount: 0,
  isFetchingPrice: false,
  showForm: true,
  formProps: {
    register: () => { },
    setValue: () => { },
    getValues: () => { },
    clearError: () => { },
    watch: () => { },
    errors: {},
  },
};

export default RoadCareCalculator;
